import React, { createContext, useContext, useState, useCallback } from 'react';
import LoadingScreen from '../components/LoadingScreen';

const LoadingContext = createContext();

/**
 * Loading Context Provider
 * Manages global loading state across the application
 */
export const LoadingProvider = ({ children }) => {
  const [loadingStates, setLoadingStates] = useState({});
  const [globalLoading, setGlobalLoading] = useState(false);
  const [globalMessage, setGlobalMessage] = useState('Loading...');

  /**
   * Set loading state for a specific component/operation
   */
  const setLoading = useCallback((key, isLoading, message = 'Loading...') => {
    setLoadingStates(prev => ({
      ...prev,
      [key]: { isLoading, message }
    }));
  }, []);

  /**
   * Set global loading state (shows full-screen loader)
   */
  const setGlobalLoadingState = useCallback((isLoading, message = 'Loading...') => {
    setGlobalLoading(isLoading);
    setGlobalMessage(message);
  }, []);

  /**
   * Check if any loading state is active
   */
  const isAnyLoading = useCallback(() => {
    if (globalLoading) return true;
    return Object.values(loadingStates).some(state => state.isLoading);
  }, [globalLoading, loadingStates]);

  /**
   * Get loading state for a specific key
   */
  const getLoading = useCallback((key) => {
    return loadingStates[key]?.isLoading || false;
  }, [loadingStates]);

  /**
   * Get loading message for a specific key
   */
  const getLoadingMessage = useCallback((key) => {
    return loadingStates[key]?.message || 'Loading...';
  }, [loadingStates]);

  const value = {
    setLoading,
    setGlobalLoadingState,
    isAnyLoading,
    getLoading,
    getLoadingMessage,
    globalLoading,
    globalMessage
  };

  return (
    <LoadingContext.Provider value={value}>
      {children}
      {/* Global Loading Screen */}
      {globalLoading && (
        <LoadingScreen 
          message={globalMessage}
          subMessage="Please wait while we process your request"
          fullScreen={true}
        />
      )}
    </LoadingContext.Provider>
  );
};

/**
 * Hook to use loading context
 */
export const useLoading = () => {
  const context = useContext(LoadingContext);
  if (!context) {
    throw new Error('useLoading must be used within a LoadingProvider');
  }
  return context;
};

/**
 * Hook for component-level loading state
 */
export const useComponentLoading = (componentKey, initialMessage = 'Loading...') => {
  const { setLoading, getLoading, getLoadingMessage } = useLoading();
  const [isLoading, setIsLoading] = useState(false);

  const startLoading = useCallback((message = initialMessage) => {
    setIsLoading(true);
    setLoading(componentKey, true, message);
  }, [componentKey, initialMessage, setLoading]);

  const stopLoading = useCallback(() => {
    setIsLoading(false);
    setLoading(componentKey, false);
  }, [componentKey, setLoading]);

  return {
    isLoading,
    startLoading,
    stopLoading,
    loadingMessage: getLoadingMessage(componentKey)
  };
};





















